import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ICertificadoSaldo } from '@interfaces/certificado-saldos.response.interface';
import { Util } from '@shared/common/utils/util';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import jszip from 'jszip';
import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';

@Injectable({
  providedIn: 'root'
})
export class CertificadoSaldosService {
  private certificadoSaldosData: ICertificadoSaldo | null = null;
  private certificadoSaldosPromise: Promise<ICertificadoSaldo> | null = null;
  private lastToken: string | null = null;

  constructor(private readonly http: HttpClient, private readonly util: Util) { }

  public getCerfificadoSaldos(token: string): Promise<ICertificadoSaldo> {
    if (this.certificadoSaldosData && this.lastToken === token) {
      return Promise.resolve(this.certificadoSaldosData);
    }

    if (this.certificadoSaldosPromise && this.lastToken === token) {
      return this.certificadoSaldosPromise;
    }

    const url = `${environment.BASE_BACKEND}/certificados/certificado-saldos`;
    const headers = this.util.getHeaders(token);

    this.lastToken = token;
    this.certificadoSaldosPromise = firstValueFrom(this.http.get<ICertificadoSaldo>(url, { headers }))
      .then((data) => {
        this.certificadoSaldosData = data;
        return data;
      })
      .finally(() => {
        this.certificadoSaldosPromise = null;
      });

    return this.certificadoSaldosPromise;
  }

  public async postCerfificadoSaldos(token: string): Promise<ICertificadoSaldo> {
    const url = `${environment.BASE_BACKEND}/certificados/certificado-saldos`;
    const headers = this.util.getHeaders(token);

    const certificado = this.certificadoSaldosData ?? (await this.getCerfificadoSaldos(token));
    const declaracion = certificado?.data?.datosCertificadoSaldo.descripcionEmision.toLowerCase() === 'original' ? 'S' : 'N';

    const body = {
      ...certificado?.data?.datosCertificadoSaldo,
      declaracion
    };

    return firstValueFrom(this.http.post<ICertificadoSaldo>(url, body, { headers })).finally(() => {
      this.certificadoSaldosData = null;
    });
  }



  public async descargarCertificadosZIP(token: string): Promise<void> {
    try {

      const response = await this.postCerfificadoSaldos(token);

      const certificados = (response?.data && 'certificados' in response.data)
        ? (response.data as any).certificados
        : [];

      if (!certificados.length) {
        console.warn('No hay certificados disponibles para descargar.');
        return;
      }

      // 🔹 2. Crear un ZIP
      const zip = new jszip();

      for (const cert of certificados) {
        const base64 = cert.documento;
        const nombreLimpio = cert.descripcionDocumento
          .replaceAll(/[^a-z0-9]/gi, '_')
          .toLowerCase();

        // 🔹 3. Convertir base64 → Blob (PDF)
        const pdfBlob = this.base64ToBlob(base64, 'application/pdf');

        // 🔹 4. Agregar al ZIP con nombre amigable
        zip.file(`${nombreLimpio}.pdf`, pdfBlob);
      }

      // 🔹 5. Generar el archivo ZIP y descargarlo
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const fecha = new Date().toISOString().slice(0, 10);
      saveAs(zipBlob, `certificados_saldos_${fecha}.zip`);
    } catch (error) {
      console.error('Error al generar ZIP de certificados:', error);
      throw error;
    }
  }

  /** 🔧 Helper para convertir Base64 → Blob */
  private base64ToBlob(base64: string, type: string): Blob {
    const byteCharacters = atob(base64);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.codePointAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type });
  }

  public async descargarCertificadosUnificados(token: string): Promise<void> {
    try {
      // 1️⃣ Invocar servicio que genera los certificados
      const response = await this.postCerfificadoSaldos(token);

      const certificados = (response?.data && 'certificados' in response.data)
        ? (response.data as any).certificados
        : [];

      if (!certificados.length) {
        console.warn('⚠️ No hay certificados disponibles para descargar.');
        return;
      }

      // 2️⃣ Definir el orden deseado según "descripcionDocumento"
      const ordenDocumentos = [
        'Comprobante Declaración',
        'CERTIFICADO DE SALDO (AGENCIA)',
        'ESTIMACION RETIRO PROGRAMADO (AGENCIA)',
        'INGRESO BASE',
        'ANEXO DEL CERTIFICADO DE SALDO (AGENCIA)',
        'ANEXO DE ESTIMACION RET. PROG. (AGENCIA)',
      ];

      // 3️⃣ Filtrar y ordenar los documentos según ese orden
      const certificadosOrdenados = ordenDocumentos
        .map(descBuscado => {
          const cert = certificados.find(
            (c: { descripcionDocumento: string }) =>
              c.descripcionDocumento?.trim().toUpperCase() === descBuscado.trim().toUpperCase()
          );
          if (!cert) {
            console.warn(`No se encontró el documento: ${descBuscado}`);
          }
          return cert;
        })
        .filter((c): c is { descripcionDocumento: string; documento: string } => !!c && !!c.documento);

      if (!certificadosOrdenados.length) {
        console.warn('No se encontraron documentos válidos para unificar.');
        return;
      }

      // 4️⃣ Crear un nuevo PDF vacío
      const pdfFinal = await PDFDocument.create();

      // 5️⃣ Iterar sobre los certificados en el orden indicado
      for (const cert of certificadosOrdenados) {
        try {
          const pdfBytes = Uint8Array.from(atob(cert.documento), c => c.codePointAt(0) as number);
          const pdfTemp = await PDFDocument.load(pdfBytes);

          const copiedPages = await pdfFinal.copyPages(pdfTemp, pdfTemp.getPageIndices());
          for (const page of copiedPages) {
            pdfFinal.addPage(page);
          }

          console.log(`Documento agregado: ${cert.descripcionDocumento}`);
        } catch (err) {
          console.error(`Error procesando ${cert.descripcionDocumento}:`, err);
        }
      }

      // 6️⃣ Serializar y descargar el PDF final
      const pdfFinalBytes = await pdfFinal.save();
      const blob = new Blob([new Uint8Array(pdfFinalBytes)], { type: 'application/pdf' });
      

      saveAs(blob, `Certificado_Saldo_Unificado.pdf`);
      console.log('PDF unificado generado correctamente.');

    } catch (error) {
      console.error('Error al generar el PDF unificado de certificados:', error);
      throw error;
    }
  }

}
